/**
 * Auth API layer — all auth flows go through the NestJS API.
 *
 * Tokens are NEVER stored or handled by the browser.
 * The API sets an HttpOnly `ghq_sid` cookie after successful auth.
 *
 * Exceptions (still Supabase direct — email-link based, no session needed):
 *   - signUp           → Supabase creates user + sends confirmation email
 *   - resetPassword    → Supabase sends password-reset email
 * These are used only to initiate flows; the resulting sessions still go
 * through the API via CallbackPage → POST /api/auth/session.
 */

import { supabase } from '@/lib/supabase';
import { apiFetch, ApiError } from './client';
import type { ApiResponse } from '@gatheredhq/types';
import type { PlanTier, VenueRole } from '@gatheredhq/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  organizationId: string | null;
  role: VenueRole | null;
  plan: PlanTier;
  onboardingCompleted: boolean;
}

// ─── Email / Password login (server-side session) ─────────────────────────────

export async function login(
  email: string,
  password: string,
): Promise<{ error: string | null; user?: AuthUser }> {
  try {
    const res = await apiFetch<ApiResponse<{ user: AuthUser }>>('/api/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (!res.success || !res.data) {
      return { error: res.error?.message ?? 'Login failed' };
    }
    return { error: null, user: res.data.user };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Login failed' };
  }
}

// ─── Google OAuth (browser PKCE → server session handoff) ────────────────────

export async function loginWithGoogle(): Promise<void> {
  // Step 1: Supabase browser client generates the OAuth URL + stores PKCE code_verifier.
  // redirectTo points to our CallbackPage which will POST tokens to /api/auth/session.
  // Use window.location.origin so this always resolves to the dashboard URL regardless of env.
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      skipBrowserRedirect: false,
    },
  });
}

// ─── Sign up (Supabase direct — sends confirmation email) ─────────────────────

export interface RegisterParams {
  fullName: string;
  email: string;
  password: string;
}

export async function register(
  params: RegisterParams,
): Promise<{ error: string | null; needsEmailConfirmation?: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email: params.email,
    password: params.password,
    options: {
      data: { full_name: params.fullName },
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) return { error: error.message };
  if (!data.session) return { error: null, needsEmailConfirmation: true };
  return { error: null };
}

// ─── Password reset (API token flow — no Supabase PKCE, no browser session) ───
// The old flow used Supabase's PKCE recovery, which breaks whenever the reset
// email is opened on a different device/browser than the one that requested it
// (the normal case). The API now issues a random token (Redis, 1h) emailed as
// `/auth/reset-password?token=…`; that token is exchanged here for the password change.

export async function requestPasswordReset(
  email: string,
): Promise<{ error: string | null }> {
  try {
    await apiFetch('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    // Backend always responds success even for unknown emails (no enumeration).
    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to send reset email' };
  }
}

/** Exchange a reset token + new password. `code` is `INVALID_TOKEN` when the link
 *  is expired/used, so the page can show a "request a new link" CTA. */
export async function resetPassword(
  token: string,
  password: string,
): Promise<{ error: string | null; code?: string }> {
  try {
    await apiFetch('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
    return { error: null };
  } catch (e) {
    if (e instanceof ApiError) return { error: e.message, code: e.code };
    return { error: e instanceof Error ? e.message : 'Failed to reset password' };
  }
}

// ─── Session handoff (called by CallbackPage after PKCE exchange) ─────────────

export async function handoffSession(
  accessToken: string,
  refreshToken: string,
): Promise<{ error: string | null; user?: AuthUser }> {
  try {
    const res = await apiFetch<ApiResponse<{ user: AuthUser }>>('/api/auth/session', {
      method: 'POST',
      body: JSON.stringify({ accessToken, refreshToken }),
    });
    if (!res.success || !res.data) {
      return { error: res.error?.message ?? 'Session handoff failed' };
    }
    return { error: null, user: res.data.user };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Session handoff failed' };
  }
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export async function logout(): Promise<void> {
  await apiFetch('/api/auth/signout', { method: 'POST' });
}

// ─── End impersonated (act-as) session — Gathered admin banner ────────────────

export async function stopImpersonation(): Promise<void> {
  await apiFetch('/api/auth/impersonation/stop', { method: 'POST' });
}

// ─── Register restaurant (session-protected via API) ─────────────────────────

export interface RegisterRestaurantParams {
  fullName: string;
  restaurantName: string;
  venueType?: string;
  serviceMode?: string;
  emirate?: string | null;
  address?: string | null;
  phone?: string | null;
  website?: string | null;
  googleMapLink?: string | null;
}

export async function registerRestaurant(
  params: RegisterRestaurantParams,
): Promise<{ error: string | null; organizationId?: string; restaurantId?: string; slug?: string }> {
  try {
    const res = await apiFetch<ApiResponse<{ organizationId: string; restaurantId: string; slug: string }>>(
      '/api/auth/register-restaurant',
      {
        method: 'POST',
        body: JSON.stringify({
          fullName:       params.fullName,
          restaurantName: params.restaurantName,
          venueType:      params.venueType ?? 'restaurant',
          serviceMode:    params.serviceMode ?? 'waiter',
          emirate:        params.emirate ?? null,
          address:        params.address ?? null,
          phone:          params.phone ?? null,
          website:        params.website ?? null,
          googleMapLink:  params.googleMapLink ?? null,
        }),
      },
    );
    if (!res.success || !res.data) {
      return { error: res.error?.message ?? 'Registration failed' };
    }
    return {
      error: null,
      organizationId: res.data.organizationId,
      restaurantId:   res.data.restaurantId,
      slug:           res.data.slug,
    };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Registration failed' };
  }
}

// ─── Invite accept flow ───────────────────────────────────────────────────────

export interface InviteDetails {
  id: string;
  email: string;
  role: 'manager';
  venueName: string;
  venueSlug: string;
  expiresAt: string;
  expired: boolean;
  alreadyAccepted: boolean;
}

export interface AcceptInviteResult {
  venueSlug: string;
  venueName: string;
}

export async function fetchInviteDetails(token: string): Promise<InviteDetails> {
  const res = await apiFetch<ApiResponse<InviteDetails>>(`/api/auth/invites/${token}`);
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Invite not found');
  return res.data;
}

export async function acceptInvite(token: string): Promise<AcceptInviteResult> {
  const res = await apiFetch<ApiResponse<AcceptInviteResult>>(`/api/auth/invites/${token}/accept`, {
    method: 'POST',
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to accept invite');
  return res.data;
}
